import React, { useState } from "react";
import {
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import FetchLoadingState from "../feedback/FetchLoadingState";

export const OrganogramNode = ({
  node,
  onEdit,
  onDelete,
  onAddChild,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const hasAssignedStaff = (node.staffMembers || []).length > 0 || node.assignedEmployeeCount > 0;

  const derivedStaffMembers =
    (node.staffMembers || []).length > 0
      ? node.staffMembers
      : Array.from({ length: node.assignedEmployeeCount || 0 }, (_, index) => ({
          id: `${node.id}-staff-${index}`,
          staffName: `Staff ${index + 1}`,
          levelName: "Unassigned Level",
        }));

  const handleDragStart = (e) => {
    e.stopPropagation();
    onDragStart(e, node);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragOver?.(e, node);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop?.(e, node);
  };

  return (
    <div className="relative flex flex-col items-center px-3 pb-6">
      <div
        className="w-full min-w-[260px] max-w-[320px] bg-white border-2 border-[#DCE4FF] rounded-lg p-3 shadow-sm hover:shadow-md cursor-move transition-shadow"
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2">
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="p-0.5 hover:bg-gray-100 rounded mt-0.5"
                >
                  {expanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                </button>
              ) : null}
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide" title={node.positionName}>
                  {node.positionName}
                </h4>
                <p className="text-xs text-gray-600 mt-1">{node.positionDescription}</p>
                <p className="text-xs text-[#1234CD] mt-2 font-medium">
                  {derivedStaffMembers.length} staff member{derivedStaffMembers.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => onAddChild(node)}
              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
              title="Add subordinate position"
            >
              <FiPlus size={16} />
            </button>
            <button
              type="button"
              onClick={() => onEdit(node)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Edit position"
            >
              <FiEdit2 size={16} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(node)}
              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete position"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        </div>

        {hasAssignedStaff ? (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            {derivedStaffMembers.map((staff) => (
              <div
                key={staff.id}
                className="rounded-md border border-gray-200 p-2 bg-gray-50"
                title={staff.staffName}
              >
                <p className="text-[11px] text-[#1234CD] font-semibold uppercase tracking-wide">
                  {staff.levelName || "Unassigned Level"}
                </p>
                <p className="text-xs text-gray-700 mt-1 truncate">{staff.staffName || "Unassigned Staff"}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {expanded && hasChildren ? (
        <div className="relative pt-7 w-full flex flex-col items-center">
          <span className="absolute top-0 h-7 border-l-2 border-[#BCCAF9]" />
          {node.children.length > 1 ? (
            <span className="absolute top-0 left-[12%] right-[12%] border-t-2 border-[#BCCAF9]" />
          ) : null}
          <div className="w-full overflow-x-auto pb-2">
            <div className="mx-auto flex min-w-max flex-nowrap justify-center gap-x-2 gap-y-4 px-2">
              {node.children.map((child) => (
                <div key={child.id} className="relative flex flex-col items-center">
                  <span className="absolute -top-7 h-7 border-l-2 border-[#BCCAF9]" />
                  <OrganogramNode
                    node={child}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onAddChild={onAddChild}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const OrganogramTree = ({
  data = [],
  onEdit,
  onDelete,
  onAddChild,
  onReorder,
  isLoading = false,
  error = null,
}) => {
  const [draggedItem, setDraggedItem] = useState(null);

  const handleDragStart = (e, node) => {
    setDraggedItem(node);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, node) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetNode) => {
    e.preventDefault();
    if (draggedItem && draggedItem.id !== targetNode.id) {
      onReorder?.(draggedItem.id, targetNode.id);
      setDraggedItem(null);
    }
  };

  if (isLoading) {
    return <FetchLoadingState message="Loading organogram..." className="min-h-[360px]" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center p-12">
        <p className="text-gray-500 mb-4">No positions in organogram yet</p>
        <button
          onClick={() => onAddChild(null)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create first position
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 overflow-x-auto">
      <div className="min-w-max">
      {data.map((rootNode) => (
        <OrganogramNode
          key={rootNode.id}
          node={rootNode}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddChild={onAddChild}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />
      ))}
      </div>
    </div>
  );
};
